import React, { useEffect, useState } from "react";
import { showToast } from "../../../utils/toast";
import {
  ModalField,
  ModalFields,
  ModalForm,
  ModalInput,
  ModalTextarea,
} from "../../../design-system/patterns/modal-form";
import { ImageUpload } from "../../../design-system/patterns/upload";

/**
 * Store info edit. One of the nine modal forms.
 *
 * The FormData payload is unchanged, including the quirk that empty values are
 * omitted rather than sent as empty strings, which is what lets a seller leave
 * the logo alone.
 *
 * THE LOGO PICKER
 * ---------------
 * `ModalFile` here was a browser-drawn "Choose file" button: no preview, no
 * stated size, no way to undo a mis-click. It is now `patterns/upload`, which
 * adds a drop zone, a preview and a remove. The request is untouched:
 * `store_logo` is still appended only when a file is chosen, so "remove" means
 * "go back to the logo already saved", never "delete my logo" — there is no
 * clear-logo flag in this API and inventing one client-side would silently ask
 * the backend for something it has no way to honour.
 *
 * The shipping policy placeholder no longer quotes a delivery window or a free
 * shipping threshold. Standing copy rule: we never state a courier, an origin
 * or a shipping price, and a placeholder is copy the same as any other.
 */

const EditStoreInfoForm = ({ open = true, initial, onSubmit, onCancel, submitting }) => {
  const [formState, setFormState] = useState({
    store_name: initial?.store_name || "",
    shipping_policy: initial?.shipping_policy || "",
  });
  const [storeLogo, setStoreLogo] = useState(null);
  const [errors, setErrors] = useState({});

  // The preview for a file that has not been uploaded yet. Revoked on replace
  // and on unmount: an object URL pins the whole File in memory until it is,
  // and a seller trying three logos in a row would leak all three.
  const [logoPreview, setLogoPreview] = useState(null);
  useEffect(() => {
    if (!storeLogo) {
      setLogoPreview(null);
      return undefined;
    }
    const url = URL.createObjectURL(storeLogo);
    setLogoPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [storeLogo]);

  // What the zone shows: the pending file if there is one, otherwise the logo
  // already on the account, so the seller can see what they are replacing.
  const currentLogo = initial?.store_logo || null;
  const shownLogo = logoPreview || currentLogo;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormState((s) => ({ ...s, [name]: value }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formState.store_name?.trim()) {
      newErrors.store_name = "Store name is required";
    }
    if (!formState.shipping_policy?.trim()) {
      newErrors.shipping_policy = "Shipping policy is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (!validateForm()) {
      showToast.error("Please fix the errors before submitting");
      return;
    }

    const formData = new FormData();

    // Append form fields
    Object.entries(formState).forEach(([key, value]) => {
      if (value) formData.append(key, value);
    });

    // Append store logo if selected
    if (storeLogo) {
      formData.append("store_logo", storeLogo);
    }

    onSubmit?.(formData);
  };

  return (
    <ModalForm
      open={open}
      title="Edit store info"
      onClose={onCancel}
      onSubmit={handleSubmit}
      submitting={submitting}
      submitLabel="Save store info"
      submittingLabel="Saving"
    >
      <ModalFields cols={1}>
        <ModalField label="Store name" error={errors.store_name}>
          <ModalInput
            name="store_name"
            value={formState.store_name}
            onChange={handleChange}
            placeholder="Enter your store name"
            required
            disabled={submitting}
          />
        </ModalField>

        <ModalField
          label="Shipping policy"
          hint="What a buyer should expect from you after they pay."
          error={errors.shipping_policy}
        >
          <ModalTextarea
            name="shipping_policy"
            value={formState.shipping_policy}
            onChange={handleChange}
            placeholder="Describe how you pack and hand over orders."
            rows={4}
            required
            disabled={submitting}
          />
        </ModalField>

        <ModalField label="Store logo" optional>
          {/* WHERE 320 COMES FROM. The logo is rendered in three places and the
              biggest of them is the profile's store-details figure, capped at
              160 x 120 by `.pfx-logo img` in Profile.css. Every other render
              crops it to a circle and is smaller: `.ds-avatar--xl` is 84px on
              the mobile store header, `.dsk-avatar` is 32px on desktop, and the
              seller cell on a listing card is smaller still. So the number that
              matters is 160 CSS px, doubled for a 2x screen. Square, because
              the avatars crop to a square and a wide logo loses its ends. */}
          <ImageUpload
            label="Store logo"
            accept="image/*"
            recommended="square, 320 × 320 px or larger"
            onFilesSelected={(e) => setStoreLogo(e.target.files?.[0] || null)}
            disabled={submitting}
            items={
              shownLogo
                ? [
                    {
                      key: shownLogo,
                      url: shownLogo,
                      alt: logoPreview ? "New store logo" : "Current store logo",
                      caption: logoPreview ? "New" : "Current",
                      // Only a not-yet-uploaded file can be taken back. There is
                      // no way to clear a saved logo through this endpoint, so
                      // offering the control would be a lie.
                      onRemove: logoPreview ? () => setStoreLogo(null) : undefined,
                      removeLabel: "Remove the new logo",
                    },
                  ]
                : []
            }
            note="It sits beside your store name, so square art reads best."
          />
        </ModalField>
      </ModalFields>
    </ModalForm>
  );
};

export default EditStoreInfoForm;
